import { Controller } from '@hotwired/stimulus';

import { isSessionExpiredResponse, reconnectHref } from '../utils/planning_frame_session.js';

/**
 * Planning Frame Session Controller
 *
 * N21 (QA A1-R-20) — dans les tiroirs du planning (frame `phase-panel-frame` du
 * panneau de phase, frame `plan_phase_form` du tiroir de formulaire), une session
 * expirée affiche le message serveur `planning.drawers.session_expired` (message +
 * lien de reconnexion) au lieu du « Content missing » brut de Turbo.
 *
 * Monté sur le wrapper de `shared/_execution_plan_document` (les trois surfaces :
 * architecte, portail MOA, espace entreprise), qui englobe les deux tiroirs :
 * `turbo:frame-missing` part du frame qui a manqué et remonte jusqu'ici.
 *
 * Le message est posé EN TÊTE du frame, sans en effacer le contenu (M2, revue
 * design system A1) : vider le frame emportait le seul bouton de fermeture du
 * tiroir, le titre qui porte l'`aria-labelledby` du dialogue
 * (`Planning::LotPanelComponent::TITLE_ID`) et la saisie en cours, et le focus
 * retombait sur `<body>`. Le focus va sur le lien de reconnexion. Une nouvelle
 * expiration dans le même frame remplace le message déjà posé (racine
 * `[data-planning-frame-session-alert]`) au lieu de l'empiler. Un squelette de
 * chargement (`[data-planning-frame-placeholder]`, cloné par
 * `phase_panel_controller.js` à l'ouverture d'une autre phase) est retiré : ce
 * n'est pas du contenu. Frame vide : le message en devient le seul enfant.
 *
 * La règle (qu'est-ce qu'une session expirée, où mène le lien) vit dans
 * `utils/planning_frame_session.js`. Le message et le lien viennent du SERVEUR
 * (`<template>`) : ni texte, ni classe, ni SVG écrits ici (DESIGN #18).
 *
 * Targets:
 * - message: `<template>` serveur, cloné en tête du frame qui a manqué ; sa racine
 *   porte `data-planning-frame-session-alert`, son lien
 *   `[data-planning-frame-session-link]` reçoit la page courante et le focus
 *
 * Values:
 * - signInPath: page de connexion de la surface (`new_user_session_path`, ou
 *   `client_portal_root_path` pour le MOA) ; sert à reconnaître une réponse qui
 *   y a atterri
 *
 * Actions:
 * - handle: `turbo:frame-missing->planning-frame-session#handle`
 *
 * @example
 * <div data-controller="planning-frame-session"
 *      data-action="turbo:frame-missing->planning-frame-session#handle"
 *      data-planning-frame-session-sign-in-path-value="/users/sign_in">
 *   <turbo-frame id="plan_phase_form"></turbo-frame>
 *   <template data-planning-frame-session-target="message">
 *     <div data-planning-frame-session-alert role="alert"><%= t('planning.drawers.session_expired.message') %>
 *       <a href="#" data-planning-frame-session-link data-turbo="false">
 *         <%= t('planning.drawers.session_expired.reconnect') %></a>
 *     </div>
 *   </template>
 * </div>
 */
export default class extends Controller {
  static targets = ['message'];
  static values = { signInPath: String };

  /**
   * Session expirée ⇒ annule le rendu « Content missing » de Turbo, pose le
   * message en tête du frame qui a manqué (contenu conservé, message précédent
   * remplacé) et y met le focus. Tout autre échec : l'événement n'est pas touché.
   * Hôte sans gabarit : no-op.
   *
   * @param {CustomEvent} event `turbo:frame-missing` (`target` = le frame,
   *   `detail.response` = le `Response` natif)
   */
  handle(event) {
    if (!this.hasMessageTarget) return;

    const response = event.detail?.response;
    if (!isSessionExpiredResponse(response, { signInPath: this.signInPathValue })) return;

    event.preventDefault();

    const fragment = this.messageTarget.content.cloneNode(true);
    const link = fragment.querySelector('[data-planning-frame-session-link]');
    if (link) link.setAttribute('href', reconnectHref(window.location.href));

    const frame = event.target;
    // Le squelette de chargement (premier chargement d'une AUTRE phase) n'est pas du
    // contenu : laissé sous le message, il pulserait et annoncerait un chargement
    // qui n'arrivera pas.
    frame.querySelector(':scope > [data-planning-frame-placeholder]')?.remove();
    const previous = frame.querySelector(':scope > [data-planning-frame-session-alert]');
    if (previous) {
      previous.replaceWith(fragment);
    } else {
      frame.prepend(fragment);
    }

    link?.focus();
  }
}
